import React from 'react';

const RevenueChart = ({ data = [], loading }) => {
  const hasData = Array.isArray(data) && data.length > 0 && data.some(d => Number(d.daily_revenue || d.monthly_revenue || 0) > 0);
  const chartItems = hasData ? data : [];

  const totalRevenueSum = chartItems.reduce((acc, d) => acc + Number(d.daily_revenue || d.monthly_revenue || 0), 0);
  const maxRevenue = Math.max(...chartItems.map(d => Number(d.daily_revenue || d.monthly_revenue || 0)), 100);

  // Compute SVG Points for dynamic curve
  const points = chartItems.map((item, i) => {
    const val = Number(item.daily_revenue || item.monthly_revenue || 0);
    const x = chartItems.length > 1 ? (i / (chartItems.length - 1)) * 440 + 30 : 250;
    const y = 140 - (val / maxRevenue) * 90;
    const label = item.day_label || item.month_label || `Point ${i + 1}`;
    return { x, y, val, label };
  });

  const pathD = points.length > 1
    ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ')
    : points.length === 1
    ? `M 30,${points[0].y} L 470,${points[0].y}`
    : '';

  const fillD = points.length > 1
    ? `${pathD} L ${points[points.length - 1].x},150 L ${points[0].x},150 Z`
    : points.length === 1
    ? `M 30,${points[0].y} L 470,${points[0].y} L 470,150 L 30,150 Z`
    : '';

  const maxPoint = points.length > 0 ? points.reduce((prev, curr) => (curr.val >= prev.val ? curr : prev), points[0]) : null;

  return (
    <div className="chart-card-box">
      <div className="card-header-flex">
        <div>
          <h3 className="card-title-text">Revenue Overview</h3>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Total Revenue: £{totalRevenueSum.toFixed(2)}</span>
        </div>
        <span style={{ fontSize: '11px', fontWeight: 800, color: '#16a34a', background: '#dcfce7', padding: '3px 10px', borderRadius: '9999px' }}>Live Trends</span>
      </div>

      <div className="chart-container-visual">
        {loading ? (
          <div style={{ height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '13px' }}>
            Loading revenue trends...
          </div>
        ) : !hasData ? (
          <div style={{ height: '160px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '13px' }}>
            <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>No revenue recorded yet</span>
            <span>Completed sales transactions will automatically plot here.</span>
          </div>
        ) : (
          <>
            <svg className="chart-svg" viewBox="0 0 500 180" preserveAspectRatio="none">
          <defs>
            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          <line x1="0" y1="30" x2="500" y2="30" stroke="#f1f5f9" strokeDasharray="4 4" />
          <line x1="0" y1="75" x2="500" y2="75" stroke="#f1f5f9" strokeDasharray="4 4" />
          <line x1="0" y1="120" x2="500" y2="120" stroke="#f1f5f9" strokeDasharray="4 4" />
          <line x1="0" y1="150" x2="500" y2="150" stroke="#e2e8f0" />

          {/* Area Fill */}
          <path d={fillD} fill="url(#revenueGradient)" />

          {/* Line Path */}
          <path d={pathD} fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

          {/* All Data Point Nodes */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="5" fill="#ffffff" stroke="#dc2626" strokeWidth="2.5" />
            </g>
          ))}

          {/* Peak Tooltip Callout */}
          {maxPoint && (
            <g>
              <rect x={Math.max(10, Math.min(maxPoint.x - 45, 400))} y={Math.max(5, maxPoint.y - 32)} width="90" height="24" rx="6" fill="#0f172a" />
              <text x={Math.max(10, Math.min(maxPoint.x - 45, 400)) + 45} y={Math.max(5, maxPoint.y - 32) + 16} textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="800">
                £{maxPoint.val.toFixed(2)}
              </text>
            </g>
          )}
        </svg>

        {/* X Axis Labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: '#64748b', fontWeight: 700, borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
          {points.map((p, i) => (
            <span key={i} style={{ flex: 1, textAlign: points.length === 1 ? 'center' : i === 0 ? 'left' : i === points.length - 1 ? 'right' : 'center' }}>
              {p.label}
            </span>
          ))}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default RevenueChart;
